import { useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from "react";
import { useCallback } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, RefreshCw } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  deferredCommitFlashClassName,
  useDeferredCommitFieldFeedback,
} from "@/components/mrp/deferredCommitFieldShared";
import { apiFetch } from "@/lib/api";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { useHubToast } from "@/components/ui/HubToast";
import { Input } from "@/components/ui/Input";
import {
  CardTitleWithHelp,
  HoverHintContent,
  HoverHintTrigger,
  PageTitleWithHelp,
  ReadOnlyHoverLayer,
} from "@/components/ui/title-help";
import { formatNumber, stripAltTlaSkuPrefixForDisplay } from "@/lib/mrp";
import { buildNavigateTargetWithTrail } from "@/lib/navigationTrail";
import { useNavigationTrail } from "@/lib/navigationTrailContext";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { cn } from "@/lib/utils";
import {
  formatTlaPlanningDate,
  TlaPlanningPoWithShadowLabel,
  TlaPlanningSortableTableHeader,
  TlaPlanningTableHeaderWithHelp,
  TLA_PLANNING_NUM_CELL_CLASS,
  type TlaPlanningDemandDetailRow,
  type TlaPlanningQtyOrderedDetailRow,
  type TlaPlanningSupplyDetailRow,
} from "./tlaPlanningShared";
import {
  buildKodiakPcbaRowGroups,
  enrichKodiakPrefixViewModelFromMatchGroups,
  kodiakTlaShortLabelsForGroupRows,
  KodiakTlaPcbaMatchTableBody,
  KodiakLineCompleteLockContextMenuPanel,
  kodiakMatchSummaryLabel,
  type KodiakLineCompleteLockContextMenuState,
  type KodiakTlaPcbaMatchRow,
} from "./kodiakTlaPcbaMatchUi";
import {
  applyKodiakPrefixAssignmentPatch,
  buildKodiakPrefixAssignmentViewModel,
  kodiakPcbaSkuCoreFeature,
  kodiakPrefixCanDropOnPcbaTarget,
  kodiakPrefixCanDropOnTlaTarget,
  KodiakUnallocatedPrefixCard,
  parseKodiakPurchaseOrderId,
  resolveKodiakPrefixDragPurchaseOrderId,
  setActiveKodiakPrefixDragPayload,
  type KodiakPrefixAssignmentBoard,
  type KodiakPrefixDragPayload,
  type KodiakTlaPrefixDropTarget,
} from "./kodiakPrefixAssignmentUi";
import {
  readKodiakMatchSessionCache,
  readKodiakMatchSessionCacheUpdatedAt,
  writeKodiakMatchSessionCache,
} from "./kodiakMatchSessionCache";
import {
  readTlaPlanningOverviewSessionCache,
  readTlaPlanningOverviewSessionCacheUpdatedAt,
  writeTlaPlanningOverviewSessionCache,
} from "./tlaPlanningOverviewSessionCache";

const PAGE_HOVER_HINT =
  "Compares TLA build-order supply (remaining capacity on Suntronic purchase-order build orders) against pending sales-order demand at each site. Demand uses create_bom_current finished-good SKUs on open sales orders, split into batched lines (batch quantity times BoM topLevel quantity minus serial_fci allocations) and unassigned lines (remaining sales order quantity not yet batched). Location 14 rolls up to SGS / Kodiak (effective location 1). Matches the Manufacturing Dashboard TLA status tab.";

const OVERVIEW_HOVER_HINT =
  "One row per TLA SKU and site. Total remaining build capacity is unallocated quantity on linked TLA build orders. Pending sales quantity is open demand. Net remaining is supply minus demand. Reorder to sets the target net remaining for each row; amount to order fills the gap when net remaining falls below that target.";

const MRP_HUB_SETTINGS_ADMIN_TITLE =
  "Module administrator access is required to change TLA reorder targets.";

const OVERVIEW_COL_LOCATION_HINT =
  "Manufacturing site where TLA supply and demand are rolled up. Megaventory location 14 counts as SGS / Kodiak (effective location 1). No site means the sales order or batch has no location.";

const OVERVIEW_COL_SKU_HINT =
  "TLA component stock keeping unit on Suntronic build orders or in open sales-order bill of materials demand. Click to open supply and demand detail.";

const OVERVIEW_COL_QTY_ORDERED_HINT =
  "Open purchase-order quantity not yet received and not yet allocated. Received is the greater of the paper receipt and the work-order hub received quantity, so devices posted on the work order before paper receipt reduce this column. Kodiak prefix allocation does the same when it is ahead of that receipt. Closed and FullyInvoiced POs contribute 0. Amount to order is reduced by this quantity. Hover for line breakdown.";

const OVERVIEW_COL_SUPPLY_REMAINING_HINT =
  "Received quantity minus consumption on the same TLA lines, including Closed and FullyInvoiced POs (Cancelled excluded). Received quantity is the greater of the paper receipt and the work-order hub received quantity. Quantity that is still open and not allocated is qty ordered, not supply. For SGS / Kodiak, consumption uses Kodiak PCBA serial attribution per supply line. Hover for line breakdown.";

const OVERVIEW_COL_PENDING_DEMAND_HINT =
  "Open TLA demand for this site that is not yet scanned into serial FCI (batched or still unassigned on open sales orders).";

const OVERVIEW_COL_NET_REMAINING_HINT =
  "Supply remaining minus pending demand. Negative means demand exceeds supply; positive means spare build capacity.";

const OVERVIEW_COL_REORDER_TO_HINT = "Target net remaining.";

const OVERVIEW_COL_AMOUNT_TO_ORDER_HINT =
  "Suggested Suntronic order quantity when net remaining falls below reorder to — reorder to minus net remaining, minus qty ordered already on open CM PO lines. Zero reorder to only suggests enough to cover a negative net.";

const KODIAK_TAB_HOVER_HINT =
  "Kodiak purchase orders: PCBA-level work orders, estimated PCBA serial prefixes, and TLA-level lines on the same PO. Serial counts use create_bom_current to map assembled finished goods to the TLA SKU on each line (same rule as TLA planning overview).";

const KODIAK_COL_PO_HINT = "Kodiak general purchase order number.";
const KODIAK_COL_PCBA_WO_HINT = "PCBA-level build order at Kodiak. PCBA prefix estimate attaches here.";
const KODIAK_COL_PCBA_PREFIX_HINT =
  "Drag stored prefix chips (for example 46550:N or 46550:B) onto the matching PCBA row, or onto a specific TLA sub-row when one PCBA build order feeds multiple TLAs (for example N-TLA-A vs N-TLA-C). A TLA-row drop assigns the prefix to the parent PCBA and locks it to that TLA for Configuration. Hover a chip for per-TLA serial counts and start/stop dates. New prefix variants from serial activity appear in Unallocated until you assign them; QC-only unknown variants show as prefix:? and can assign to any N/Q/B PCBA row.";
const KODIAK_COL_TLA_WO_HINT =
  "TLA-level build order on the same purchase order. Drop a prefix chip on this row to lock it to this TLA while keeping the parent PCBA assignment. Empty for PCBA-only lines.";
const KODIAK_COL_TLA_SKU_HINT =
  "TLA component SKU on this build order. Drop a prefix chip here to lock that prefix to this TLA (Configuration shows the planned prefix TLA when the PCBA serial is scanned). Serials count toward this line when the finished good BoM topLevel TLA matches.";
const KODIAK_COL_SERIAL_COUNT_HINT =
  "Distinct PCBA serials allocated to this TLA line via finished-good BoM. PCBA QC without a configured finished SKU appears in the unallocated QC sub-row when multiple TLAs share the prefix. When the work order is counted as fully assigned (red padlock, public.build_order.count_as_assigned), the cell shows scanned count / work order quantity; planning remaining is zero. Right-click a TLA line serial count (MRP admin) to count as fully assigned or clear that flag.";

const STICKY_COL_CLASS =
  "sticky left-0 z-10 whitespace-nowrap bg-white px-1.5 py-1.5 after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-slate-200";

const OVERVIEW_ROW_SELECTED_CLASS = "bg-sky-50 ring-1 ring-inset ring-sky-200";

const OVERVIEW_NUM_CELL_CLASS = "w-[4.25rem] px-1.5 py-1.5 text-right tabular-nums";

const OVERVIEW_SKU_CELL_CLASS = "max-w-[10rem] px-1.5 py-1.5 text-left";

const OVERVIEW_HEADER_NUM_LABEL_CLASS = "max-w-[4.25rem] text-xs leading-snug";

const OVERVIEW_HEADER_LOCATION_LABEL_CLASS = "max-w-[5.5rem] text-xs leading-snug";

const OVERVIEW_HEADER_SKU_LABEL_CLASS = "max-w-[7rem] text-xs leading-snug";

const KODIAK_MATCH_QUERY_KEY = ["mrp", "tla-planning", "kodiak-pcba-match"] as const;

type KodiakPcbaMatchQueryData = {
  rows: KodiakTlaPcbaMatchRow[];
  prefix_board: KodiakPrefixAssignmentBoard;
  line_complete_locks?: number[];
  cached_at?: string;
  stale?: boolean;
  revalidating?: boolean;
  next_refresh_after?: string | null;
};

type TlaPlanningCapacityQueryData = {
  rows: TlaPlanningCapacityRow[];
  cached_at?: string;
  stale?: boolean;
  revalidating?: boolean;
  next_refresh_after?: string | null;
};

type TlaPlanningLineDetailsQueryData = TlaPlanningOverviewLineDetails & {
  cached_at?: string;
  stale?: boolean;
  revalidating?: boolean;
  next_refresh_after?: string | null;
};

async function fetchKodiakPcbaMatch(options?: { background?: boolean; refresh?: boolean }): Promise<KodiakPcbaMatchQueryData> {
  return apiFetch<KodiakPcbaMatchQueryData>("/api/mrp/tla-planning/kodiak-pcba-match", {
    query: {
      ...(options?.background ? { background: "1" } : {}),
      ...(options?.refresh ? { refresh: "1" } : {}),
    },
  });
}

async function fetchTlaPlanningCapacity(options?: {
  background?: boolean;
  refresh?: boolean;
}): Promise<TlaPlanningCapacityQueryData> {
  return apiFetch<TlaPlanningCapacityQueryData>("/api/mrp/tla-planning/capacity", {
    query: {
      ...(options?.background ? { background: "1" } : {}),
      ...(options?.refresh ? { refresh: "1" } : {}),
    },
  });
}

async function fetchTlaPlanningOverviewLineDetails(options?: {
  background?: boolean;
  refresh?: boolean;
}): Promise<TlaPlanningLineDetailsQueryData> {
  return apiFetch<TlaPlanningLineDetailsQueryData>("/api/mrp/tla-planning/overview-line-details", {
    query: {
      ...(options?.background ? { background: "1" } : {}),
      ...(options?.refresh ? { refresh: "1" } : {}),
    },
  });
}

type TlaPlanningTab = "overview" | "kodiak";

type KodiakSortKey =
  | "po_number"
  | "po_date"
  | "po_quantity"
  | "pcba_work_order_number"
  | "pcba_sku"
  | "pcba_work_order_qty"
  | "pcba_prefix_estimate"
  | "tla_work_order_number"
  | "tla_sku"
  | "tla_work_order_qty"
  | "unique_serial_count";

interface TlaPlanningCapacityRow {
  effective_location_id: number;
  location_name: string;
  sku: string;
  qty_ordered?: number;
  total_remaining_build_capacity: number;
  pending_sales_quantity: number;
  additional_build_orders_needed: number;
  remaining_after_pending_sales: number;
  net_remaining: number;
  reorder_to: number;
  amount_to_order: number;
}

interface TlaPlanningOverviewLineDetails {
  supply_by_key: Record<string, TlaPlanningSupplyDetailRow[]>;
  demand_by_key: Record<string, TlaPlanningDemandDetailRow[]>;
  qty_ordered_by_key: Record<string, TlaPlanningQtyOrderedDetailRow[]>;
}

function compareKodiakRows(
  left: KodiakTlaPcbaMatchRow,
  right: KodiakTlaPcbaMatchRow,
  sortKey: KodiakSortKey,
  sortDir: "asc" | "desc",
): number {
  const direction = sortDir === "asc" ? 1 : -1;
  const compareText = (a: string | null, b: string | null) =>
    (a ?? "").localeCompare(b ?? "") * direction;
  const compareNumber = (a: number | null, b: number | null) =>
    ((a ?? Number.NEGATIVE_INFINITY) - (b ?? Number.NEGATIVE_INFINITY)) * direction;

  switch (sortKey) {
    case "po_number":
      return compareText(left.po_number, right.po_number);
    case "po_date":
      return compareText(left.po_date, right.po_date);
    case "po_quantity":
      return compareNumber(left.po_quantity, right.po_quantity);
    case "pcba_work_order_number":
      return compareText(left.pcba_work_order_number, right.pcba_work_order_number);
    case "pcba_sku":
      return compareText(left.pcba_sku, right.pcba_sku);
    case "pcba_work_order_qty":
      return compareNumber(left.pcba_work_order_qty, right.pcba_work_order_qty);
    case "pcba_prefix_estimate":
      return compareText(left.pcba_prefix_estimate, right.pcba_prefix_estimate);
    case "tla_work_order_number":
      return compareText(left.tla_work_order_number, right.tla_work_order_number);
    case "tla_sku":
      return compareText(left.tla_sku, right.tla_sku);
    case "tla_work_order_qty":
      return compareNumber(left.tla_work_order_qty, right.tla_work_order_qty);
    case "unique_serial_count":
      return compareNumber(left.unique_serial_count, right.unique_serial_count);
    default:
      return 0;
  }
}

function tlaPlanningRowLocationId(row: TlaPlanningCapacityRow): number | null {
  const locationId = Number(row.effective_location_id);
  return Number.isFinite(locationId) && locationId > 0 ? locationId : null;
}

function tlaPlanningOverviewLineLocationId(row: TlaPlanningCapacityRow): number {
  return tlaPlanningRowLocationId(row) ?? 0;
}

function tlaPlanningOverviewRowKey(row: TlaPlanningCapacityRow): string {
  return `${row.effective_location_id}:${row.sku}`;
}

type TlaPlanningCapacityCache = { rows: TlaPlanningCapacityRow[] };

function applyTlaPlanningReorderToToCapacityRows(
  rows: TlaPlanningCapacityRow[],
  sku: string,
  locationId: number,
  reorderTo: number,
): TlaPlanningCapacityRow[] {
  return rows.map((entry) => {
    if (entry.sku !== sku || Number(entry.effective_location_id) !== locationId) {
      return entry;
    }
    const netRemaining = entry.net_remaining;
    const qtyOrdered = entry.qty_ordered ?? 0;
    const amountToOrder = Math.max(
      0,
      (netRemaining >= reorderTo ? 0 : reorderTo - netRemaining) - qtyOrdered,
    );
    return {
      ...entry,
      reorder_to: reorderTo,
      amount_to_order: amountToOrder,
    };
  });
}

function tlaPlanningStickyCellClass(selected: boolean): string {
  return cn(STICKY_COL_CLASS, selected ? "bg-sky-50" : undefined);
}

function tlaPlanningOverviewKey(sku: string, locationId: number): string {
  return `${sku.trim()}\u0001${locationId}`;
}

function tlaPlanningCellShowsHoverBreakdown(value: number | null | undefined): boolean {
  return (value ?? 0) !== 0;
}

function TlaPlanningHoverTable({
  headers,
  rows,
  numericColumnIndexes,
}: {
  headers: string[];
  rows: ReactNode[][];
  numericColumnIndexes: number[];
}): JSX.Element {
  if (rows.length === 0) {
    return <span className="text-slate-600">No line breakdown.</span>;
  }
  return (
    <table className="min-w-[18rem] border-collapse text-[11px] leading-snug">
      <thead>
        <tr className="text-slate-500">
          {headers.map((header, headerIndex) => (
            <th
              key={header}
              className={cn(
                "px-1.5 py-0.5 text-left font-medium first:pl-0 last:pr-0",
                numericColumnIndexes.includes(headerIndex) ? "text-right" : undefined,
              )}
            >
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((cells, rowIndex) => (
          <tr key={rowIndex} className="border-t border-slate-100 text-slate-800">
            {cells.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                className={cn(
                  "px-1.5 py-0.5 align-top first:pl-0 last:pr-0",
                  numericColumnIndexes.includes(cellIndex) ? "text-right tabular-nums" : undefined,
                )}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TlaPlanningQtyOrderedCell({
  row,
  qtyOrderedLines,
  linesPending,
  linesError,
}: {
  row: TlaPlanningCapacityRow;
  qtyOrderedLines: TlaPlanningQtyOrderedDetailRow[] | undefined;
  linesPending: boolean;
  linesError: Error | null;
}) {
  const qtyOrdered = row.qty_ordered ?? 0;

  const cellContent = (
    <span
      className={cn(
        OVERVIEW_NUM_CELL_CLASS,
        "block",
        qtyOrdered > 0 ? "font-medium text-slate-800" : "text-slate-400",
      )}
      onClick={(event) => event.stopPropagation()}
    >
      {qtyOrdered > 0 ? formatNumber(qtyOrdered) : "—"}
    </span>
  );

  if (!tlaPlanningCellShowsHoverBreakdown(qtyOrdered)) {
    return cellContent;
  }

  const hintBody = (() => {
    if (linesPending) {
      return <span className="text-slate-600">Loading qty ordered lines…</span>;
    }
    if (linesError != null) {
      return <span className="text-red-700">{linesError.message}</span>;
    }
    const lines = qtyOrderedLines ?? [];
    if (lines.length === 0) {
      return <span className="text-slate-600">No open CM purchase order lines for this SKU at this site.</span>;
    }
    return (
      <TlaPlanningHoverTable
        headers={["PO", "WO", "Ordered", "Received", "Scanned", "Remaining", "Date"]}
        numericColumnIndexes={[2, 3, 4, 5]}
        rows={lines.map((line) => [
          <TlaPlanningPoWithShadowLabel
            key={`${line.purchase_order}-po`}
            purchaseOrder={line.purchase_order}
            isShadowReconciliation={line.is_shadow_reconciliation}
          />,
          line.work_order_number ?? "—",
          formatNumber(line.ordered_quantity),
          formatNumber(line.received_quantity),
          formatNumber(line.scanned_quantity),
          formatNumber(line.qty_ordered),
          formatTlaPlanningDate(line.submitted_date),
        ])}
      />
    );
  })();

  return (
    <HoverHintContent hoverHint={hintBody} align="end" block className="!flex-none w-full">
      {cellContent}
    </HoverHintContent>
  );
}

function TlaPlanningSupplyRemainingCell({
  row,
  supplyLines,
  linesPending,
  linesError,
}: {
  row: TlaPlanningCapacityRow;
  supplyLines: TlaPlanningSupplyDetailRow[] | undefined;
  linesPending: boolean;
  linesError: Error | null;
}) {
  const supplyRemaining = row.total_remaining_build_capacity;

  const cellContent = (
    <span className={cn(OVERVIEW_NUM_CELL_CLASS, "block cursor-default")}>
      {formatNumber(supplyRemaining)}
    </span>
  );

  if (!tlaPlanningCellShowsHoverBreakdown(supplyRemaining)) {
    return cellContent;
  }

  const hintBody = (() => {
    if (linesPending) {
      return <span className="text-slate-600">Loading supply lines…</span>;
    }
    if (linesError != null) {
      return <span className="text-red-700">{linesError.message}</span>;
    }
    const lines = (supplyLines ?? []).filter((line) => line.remaining > 0);
    if (lines.length === 0) {
      return <span className="text-slate-600">No supply remaining on open work orders.</span>;
    }
    return (
      <TlaPlanningHoverTable
        headers={["PO", "WO", "Ordered", "Received", "Scanned", "Remaining"]}
        numericColumnIndexes={[2, 3, 4, 5]}
        rows={lines.map((line) => [
          <TlaPlanningPoWithShadowLabel
            key={`${line.purchase_order}-po`}
            purchaseOrder={line.purchase_order}
            isShadowReconciliation={line.is_shadow_reconciliation}
          />,
          line.work_order_number ?? "—",
          formatNumber(line.work_order_quantity),
          formatNumber(line.ordered),
          formatNumber(line.allocated),
          formatNumber(line.remaining),
        ])}
      />
    );
  })();

  return (
    <HoverHintContent hoverHint={hintBody} align="end" block className="!flex-none w-full">
      {cellContent}
    </HoverHintContent>
  );
}

function TlaPlanningPendingDemandCell({
  row,
  demandLines,
  linesPending,
  linesError,
}: {
  row: TlaPlanningCapacityRow;
  demandLines: TlaPlanningDemandDetailRow[] | undefined;
  linesPending: boolean;
  linesError: Error | null;
}) {
  const pendingDemand = row.pending_sales_quantity;

  const cellContent = (
    <span className={cn(OVERVIEW_NUM_CELL_CLASS, "block cursor-default")}>
      {formatNumber(pendingDemand)}
    </span>
  );

  if (!tlaPlanningCellShowsHoverBreakdown(pendingDemand)) {
    return cellContent;
  }

  const hintBody = (() => {
    if (linesPending) {
      return <span className="text-slate-600">Loading demand lines…</span>;
    }
    if (linesError != null) {
      return <span className="text-red-700">{linesError.message}</span>;
    }
    const lines = demandLines ?? [];
    return (
      <TlaPlanningHoverTable
        headers={["SO", "Batch", "WO", "Qty", "Ship by"]}
        numericColumnIndexes={[3]}
        rows={lines.map((line) => [
          line.sales_order_number || "—",
          line.batch_id ?? "—",
          line.work_order_number ?? "—",
          formatNumber(line.pending),
          formatTlaPlanningDate(line.sgs_ship_by_date),
        ])}
      />
    );
  })();

  return (
    <HoverHintContent hoverHint={hintBody} align="end" block className="!flex-none w-full">
      {cellContent}
    </HoverHintContent>
  );
}

function tlaPlanningReorderToDisplayValue(row: TlaPlanningCapacityRow): number {
  return row.reorder_to;
}

function TlaPlanningReorderToCell({
  row,
  canEdit,
  saving,
  commitError,
  commitSucceeded,
  onCommit,
  onInvalidCommit,
}: {
  row: TlaPlanningCapacityRow;
  canEdit: boolean;
  saving: boolean;
  commitError: string | null;
  commitSucceeded: boolean;
  onCommit: (reorderTo: number) => void;
  onInvalidCommit: (message: string) => void;
}) {
  const locationId = tlaPlanningRowLocationId(row);
  const displayValue = tlaPlanningReorderToDisplayValue(row);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(displayValue));
  const flashTone = useDeferredCommitFieldFeedback(commitSucceeded, commitError, {
    playAudio: false,
    feedbackId: tlaPlanningOverviewRowKey(row),
  });

  useEffect(() => {
    if (!editing) {
      setDraft(String(tlaPlanningReorderToDisplayValue(row)));
    }
  }, [row.reorder_to, editing]);

  function enterEditMode(): void {
    if (!canEdit || locationId == null || saving) {
      return;
    }
    setDraft(String(displayValue));
    setEditing(true);
  }

  function cancelEdit(): void {
    setDraft(String(displayValue));
    setEditing(false);
  }

  function commitDraft(): void {
    if (locationId == null || saving) {
      setEditing(false);
      return;
    }
    const parsed = Number.parseInt(draft.trim(), 10);
    if (!Number.isInteger(parsed) || parsed < 0) {
      onInvalidCommit("Reorder to must be a whole number zero or greater.");
      cancelEdit();
      return;
    }
    if (parsed !== displayValue) {
      onCommit(parsed);
    }
    setEditing(false);
  }

  const editLabel = `Edit reorder to for ${row.sku}`;

  const editableValue =
    editing ? (
      <div className="inline-flex items-center justify-end gap-1">
        <Input
          type="number"
          min={0}
          step={1}
          inputMode="numeric"
          value={draft}
          disabled={saving}
          aria-label={editLabel}
          className={cn(
            "h-8 w-14 text-right tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
            deferredCommitFlashClassName(flashTone),
          )}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Enter") {
              event.preventDefault();
              commitDraft();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              cancelEdit();
            }
          }}
          autoFocus
        />
        {saving ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-500" aria-hidden /> : null}
      </div>
    ) : (
      <div
        className={cn(
          "inline-flex items-center justify-end gap-1 rounded px-1 -mx-1",
          deferredCommitFlashClassName(flashTone),
        )}
      >
        <button
          type="button"
          className="tabular-nums text-slate-900 hover:text-brand-700 disabled:opacity-50"
          onClick={(event) => {
            event.stopPropagation();
            enterEditMode();
          }}
          onMouseDown={(event) => event.stopPropagation()}
          disabled={saving || locationId == null}
          aria-label={editLabel}
        >
          {formatNumber(displayValue)}
        </button>
        <HoverHintTrigger
          trigger={
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                enterEditMode();
              }}
              onMouseDown={(event) => event.stopPropagation()}
              disabled={saving || locationId == null}
              className="shrink-0 rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50"
              aria-label={editLabel}
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </button>
          }
        >
          {editLabel}
        </HoverHintTrigger>
      </div>
    );

  return (
    <td
      className={cn(OVERVIEW_NUM_CELL_CLASS, "align-middle")}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {locationId == null ? (
        <span className="text-slate-400">—</span>
      ) : canEdit ? (
        editableValue
      ) : (
        <ReadOnlyHoverLayer active hoverHint={MRP_HUB_SETTINGS_ADMIN_TITLE} layout="inline">
          <span
            className={cn(
              "block rounded px-1 -mx-1 tabular-nums",
              deferredCommitFlashClassName(flashTone),
            )}
          >
            {formatNumber(displayValue)}
          </span>
        </ReadOnlyHoverLayer>
      )}
    </td>
  );
}

export function TlaPlanningPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: TlaPlanningTab = searchParams.get("tab") === "kodiak" ? "kodiak" : "overview";
  const [kodiakSessionBootstrap] = useState(() => ({
    payload: readKodiakMatchSessionCache(),
    updatedAt: readKodiakMatchSessionCacheUpdatedAt(),
  }));
  const [overviewSessionBootstrap] = useState(() => ({
    payload: readTlaPlanningOverviewSessionCache(),
    updatedAt: readTlaPlanningOverviewSessionCacheUpdatedAt(),
  }));
  const [selectedOverviewRowKey, setSelectedOverviewRowKey] = useState<string | null>(null);
  const [kodiakSortKey, setKodiakSortKey] = useState<KodiakSortKey>("po_date");
  const [kodiakSortDir, setKodiakSortDir] = useState<"asc" | "desc">("desc");
  const [reorderCommitFeedbackByKey, setReorderCommitFeedbackByKey] = useState<
    Record<string, { error?: string; succeeded?: boolean }>
  >({});
  const navigate = useNavigate();
  const { trail } = useNavigationTrail();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const hubMrpAdminAllowed = useHubModuleAdmin("mrp");
  const lastCapacityErrorRef = useRef<string | null>(null);
  const lastLineDetailsErrorRef = useRef<string | null>(null);

  const capacityQuery = useQuery({
    queryKey: ["mrp", "tla-planning", "capacity"],
    queryFn: () => fetchTlaPlanningCapacity({ background: true }),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    initialData: overviewSessionBootstrap.payload
      ? {
          rows: overviewSessionBootstrap.payload.rows,
        }
      : undefined,
    initialDataUpdatedAt:
      overviewSessionBootstrap.payload != null && overviewSessionBootstrap.updatedAt > 0
        ? overviewSessionBootstrap.updatedAt
        : undefined,
  });

  const lineDetailsQuery = useQuery({
    queryKey: ["mrp", "tla-planning", "overview-line-details"],
    queryFn: () => fetchTlaPlanningOverviewLineDetails({ background: true }),
    enabled: capacityQuery.isSuccess || overviewSessionBootstrap.payload != null,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    initialData: overviewSessionBootstrap.payload?.line_details,
    initialDataUpdatedAt:
      overviewSessionBootstrap.payload != null && overviewSessionBootstrap.updatedAt > 0
        ? overviewSessionBootstrap.updatedAt
        : undefined,
  });

  const kodiakMatchQuery = useQuery<KodiakPcbaMatchQueryData>({
    queryKey: KODIAK_MATCH_QUERY_KEY,
    queryFn: () => fetchKodiakPcbaMatch({ background: true }),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
    initialData: kodiakSessionBootstrap.payload,
    initialDataUpdatedAt:
      kodiakSessionBootstrap.payload != null && kodiakSessionBootstrap.updatedAt > 0
        ? kodiakSessionBootstrap.updatedAt
        : undefined,
  });

  const overviewRefreshing =
    capacityQuery.isFetching || lineDetailsQuery.isFetching || kodiakMatchQuery.isFetching;

  const refreshTlaPlanningOverview = async (): Promise<void> => {
    await Promise.all([
      fetchTlaPlanningCapacity({ refresh: true }),
      fetchTlaPlanningOverviewLineDetails({ refresh: true }),
      fetchKodiakPcbaMatch({ refresh: true, background: true }),
    ]);
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ["mrp", "tla-planning", "capacity"] }),
      queryClient.refetchQueries({ queryKey: ["mrp", "tla-planning", "overview-line-details"] }),
      queryClient.refetchQueries({ queryKey: KODIAK_MATCH_QUERY_KEY }),
    ]);
  };

  useHubCacheStream({
    channels: [
      HUB_CACHE_CHANNEL.mrpTlaPlanningOverview,
      HUB_CACHE_CHANNEL.mrpTlaPlanningKodiakPcbaMatch,
    ],
    onEvent: (event) => {
      if (event.phase !== "ready" && event.phase !== "invalidated") {
        return;
      }
      if (event.channel === HUB_CACHE_CHANNEL.mrpTlaPlanningOverview) {
        void queryClient.invalidateQueries({ queryKey: ["mrp", "tla-planning", "capacity"] });
        void queryClient.invalidateQueries({ queryKey: ["mrp", "tla-planning", "overview-line-details"] });
        return;
      }
      if (event.channel === HUB_CACHE_CHANNEL.mrpTlaPlanningKodiakPcbaMatch) {
        void queryClient.invalidateQueries({ queryKey: KODIAK_MATCH_QUERY_KEY });
      }
    },
  });

  useLayoutEffect(() => {
    const cached = readKodiakMatchSessionCache();
    if (cached != null && queryClient.getQueryData(KODIAK_MATCH_QUERY_KEY) == null) {
      queryClient.setQueryData(KODIAK_MATCH_QUERY_KEY, cached);
    }
    const overviewCached = readTlaPlanningOverviewSessionCache();
    if (overviewCached != null) {
      if (queryClient.getQueryData(["mrp", "tla-planning", "capacity"]) == null) {
        queryClient.setQueryData(["mrp", "tla-planning", "capacity"], { rows: overviewCached.rows });
      }
      if (queryClient.getQueryData(["mrp", "tla-planning", "overview-line-details"]) == null) {
        queryClient.setQueryData(["mrp", "tla-planning", "overview-line-details"], overviewCached.line_details);
      }
    }
  }, [queryClient]);

  useEffect(() => {
    void queryClient.prefetchQuery({
      queryKey: KODIAK_MATCH_QUERY_KEY,
      queryFn: () => fetchKodiakPcbaMatch({ background: true }),
      staleTime: 5 * 60 * 1000,
    });
  }, [queryClient]);

  useEffect(() => {
    if (kodiakMatchQuery.data?.revalidating !== true) {
      return;
    }
    const timer = window.setInterval(() => {
      void fetchKodiakPcbaMatch().then((data) => {
        queryClient.setQueryData<KodiakPcbaMatchQueryData>(KODIAK_MATCH_QUERY_KEY, data);
      });
    }, 2500);
    return () => window.clearInterval(timer);
  }, [kodiakMatchQuery.data?.revalidating, queryClient]);

  useEffect(() => {
    if (capacityQuery.data?.rows == null || lineDetailsQuery.data == null) {
      return;
    }
    if (!capacityQuery.isSuccess || !lineDetailsQuery.isSuccess) {
      return;
    }
    writeTlaPlanningOverviewSessionCache({
      rows: capacityQuery.data.rows,
      line_details: {
        supply_by_key: lineDetailsQuery.data.supply_by_key,
        demand_by_key: lineDetailsQuery.data.demand_by_key,
        qty_ordered_by_key: lineDetailsQuery.data.qty_ordered_by_key ?? {},
      },
    });
  }, [capacityQuery.data, capacityQuery.isSuccess, lineDetailsQuery.data, lineDetailsQuery.isSuccess]);

  useEffect(() => {
    if (kodiakMatchQuery.data?.rows == null || kodiakMatchQuery.data.prefix_board == null) return;
    if (!kodiakMatchQuery.isSuccess) return;
    writeKodiakMatchSessionCache({
      rows: kodiakMatchQuery.data.rows,
      prefix_board: kodiakMatchQuery.data.prefix_board,
      line_complete_locks: kodiakMatchQuery.data.line_complete_locks ?? [],
    });
  }, [kodiakMatchQuery.data, kodiakMatchQuery.isSuccess]);

  const kodiakMatchRows = kodiakMatchQuery.data?.rows;
  const kodiakPrefixBoard = kodiakMatchQuery.data?.prefix_board;
  const kodiakHasDisplayableData =
    kodiakMatchQuery.data?.rows != null && kodiakMatchQuery.data.prefix_board != null;
  const kodiakShowRefreshing =
    kodiakMatchQuery.isFetching || kodiakMatchQuery.data?.revalidating === true;
  const kodiakShowBlockingLoad = !kodiakHasDisplayableData && kodiakMatchQuery.isFetching;

  const kodiakPrefixAssignmentMutation = useMutation({
    mutationFn: (body: {
      purchase_order_id: number;
      pcba_prefix: string;
      core_feature: string;
      pcba_build_order_id: number | null;
      tla_build_order_id?: number | null;
      tla_sku?: string | null;
      tla_work_order_number?: string | null;
    }) =>
      apiFetch<{
        ok: true;
        purchase_order_id: number;
        pcba_prefix: string;
        pcba_build_order_id: number | null;
        core_feature?: string;
      }>("/api/mrp/tla-planning/kodiak-pcba-prefix-assignment", {
        method: "PATCH",
        body,
      }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: KODIAK_MATCH_QUERY_KEY });
      const previous = queryClient.getQueryData<{
        rows: KodiakTlaPcbaMatchRow[];
        prefix_board: KodiakPrefixAssignmentBoard;
      }>(KODIAK_MATCH_QUERY_KEY);
      if (previous?.prefix_board != null) {
        queryClient.setQueryData(KODIAK_MATCH_QUERY_KEY, {
          ...previous,
          prefix_board: applyKodiakPrefixAssignmentPatch(previous.prefix_board, variables),
        });
      }
      return { previous };
    },
    onSuccess: (_data, variables) => {
      const current = queryClient.getQueryData<{
        rows: KodiakTlaPcbaMatchRow[];
        prefix_board: KodiakPrefixAssignmentBoard;
        line_complete_locks?: number[];
      }>(KODIAK_MATCH_QUERY_KEY);
      if (current?.prefix_board != null) {
        writeKodiakMatchSessionCache({
          rows: current.rows,
          prefix_board: current.prefix_board,
          line_complete_locks: current.line_complete_locks ?? [],
        });
      }
      if (variables.pcba_build_order_id == null) {
        showToast({ tone: "success", message: "Prefix unassigned." });
        return;
      }
      if (variables.tla_build_order_id != null && variables.tla_build_order_id > 0) {
        showToast({
          tone: "success",
          message: `Prefix ${variables.pcba_prefix}:${variables.core_feature} assigned and locked to TLA row.`,
        });
        return;
      }
      showToast({
        tone: "success",
        message: `Prefix ${variables.pcba_prefix}:${variables.core_feature} assigned to PCBA (group TLA lock).`,
      });
    },
    onError: (error: unknown, _variables, context) => {
      if (context?.previous != null) {
        queryClient.setQueryData(KODIAK_MATCH_QUERY_KEY, context.previous);
      }
      const message = error instanceof Error ? error.message : "Could not save prefix assignment.";
      showToast({ tone: "error", message });
    },
  });

  const kodiakManualPrefixMutation = useMutation({
    mutationFn: (body: { pcba_prefix: string }) =>
      apiFetch<{ ok: true; pcba_prefix: string; core_feature: string }>(
        "/api/mrp/tla-planning/kodiak-pcba-prefix-manual",
        { method: "POST", body },
      ),
    onSuccess: (_data, variables) => {
      const current = queryClient.getQueryData<{
        rows: KodiakTlaPcbaMatchRow[];
        prefix_board: KodiakPrefixAssignmentBoard;
        line_complete_locks?: number[];
      }>(KODIAK_MATCH_QUERY_KEY);
      if (current?.prefix_board != null) {
        writeKodiakMatchSessionCache({
          rows: current.rows,
          prefix_board: current.prefix_board,
          line_complete_locks: current.line_complete_locks ?? [],
        });
      }
      showToast({ tone: "success", message: `Prefix ${variables.pcba_prefix}:? added to unallocated.` });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Could not register prefix.";
      showToast({ tone: "error", message });
    },
  });

  const kodiakLineCompleteLockMutation = useMutation({
    mutationFn: async (input: { tla_build_order_id: number; lock: boolean }) => {
      if (input.lock) {
        return apiFetch<{ ok: true; tla_build_order_id: number }>(
          "/api/mrp/tla-planning/kodiak-tla-line-complete-lock",
          {
            method: "PUT",
            body: { tla_build_order_id: input.tla_build_order_id },
          },
        );
      }
      const params = new URLSearchParams({
        tla_build_order_id: String(input.tla_build_order_id),
      });
      return apiFetch<{ ok: true; tla_build_order_id: number }>(
        `/api/mrp/tla-planning/kodiak-tla-line-complete-lock?${params.toString()}`,
        { method: "DELETE" },
      );
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: KODIAK_MATCH_QUERY_KEY });
      const previous = queryClient.getQueryData<KodiakPcbaMatchQueryData>(KODIAK_MATCH_QUERY_KEY);
      if (previous != null) {
        const nextLocks = new Set(previous.line_complete_locks ?? []);
        if (variables.lock) {
          nextLocks.add(variables.tla_build_order_id);
        } else {
          nextLocks.delete(variables.tla_build_order_id);
        }
        queryClient.setQueryData(KODIAK_MATCH_QUERY_KEY, {
          ...previous,
          line_complete_locks: [...nextLocks],
        });
      }
      return { previous };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KODIAK_MATCH_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "tla-planning", "capacity"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "tla-planning", "overview-line-details"] });
    },
    onError: (error: unknown, _variables, context) => {
      if (context?.previous != null) {
        queryClient.setQueryData(KODIAK_MATCH_QUERY_KEY, context.previous);
      }
      const message = error instanceof Error ? error.message : "Could not update complete lock.";
      showToast({ tone: "error", message });
    },
  });

  const persistKodiakPrefixAssignment = (
    purchase_order_id: number,
    pcba_prefix: string,
    core_feature: string,
    pcba_build_order_id: number | null,
    tla_build_order_id?: number | null,
    tla_sku?: string | null,
    tla_work_order_number?: string | null,
  ): void => {
    const normalizedPurchaseOrderId = parseKodiakPurchaseOrderId(purchase_order_id);
    if (normalizedPurchaseOrderId == null) {
      showToast({ tone: "error", message: "Could not resolve purchase order for this prefix assignment." });
      return;
    }
    kodiakPrefixAssignmentMutation.mutate({
      purchase_order_id: normalizedPurchaseOrderId,
      pcba_prefix,
      core_feature,
      pcba_build_order_id,
      tla_build_order_id: tla_build_order_id ?? null,
      tla_sku: tla_sku ?? null,
      tla_work_order_number: tla_work_order_number ?? null,
    });
  };

  const capacityRows = capacityQuery.data?.rows ?? [];
  const lineDetailsError = lineDetailsQuery.error as Error | null;

  const sortedKodiakRows = useMemo(() => {
    const rows = kodiakMatchRows ?? [];
    return [...rows].sort((left, right) => compareKodiakRows(left, right, kodiakSortKey, kodiakSortDir));
  }, [kodiakMatchRows, kodiakSortDir, kodiakSortKey]);

  const kodiakPcbaGroups = useMemo(() => buildKodiakPcbaRowGroups(sortedKodiakRows), [sortedKodiakRows]);

  const kodiakPrefixViewModel = useMemo(() => {
    const base = buildKodiakPrefixAssignmentViewModel(kodiakPrefixBoard);
    return enrichKodiakPrefixViewModelFromMatchGroups(
      base,
      kodiakPrefixBoard?.assignments ?? [],
      kodiakPcbaGroups,
      kodiakPrefixBoard,
    );
  }, [kodiakPrefixBoard, kodiakPcbaGroups]);

  const kodiakLineCompleteLockIds = useMemo(() => {
    const ids = kodiakMatchQuery.data?.line_complete_locks ?? [];
    return new Set(ids.map((id) => Number(id)));
  }, [kodiakMatchQuery.data?.line_complete_locks]);

  const [kodiakLineCompleteLockMenu, setKodiakLineCompleteLockMenu] =
    useState<KodiakLineCompleteLockContextMenuState>(null);

  const handleKodiakSerialCountContextMenu = useCallback(
    (
      event: React.MouseEvent<HTMLTableCellElement>,
      target: { tlaBuildOrderId: number; locked: boolean },
    ) => {
      setKodiakLineCompleteLockMenu({
        clientX: event.clientX,
        clientY: event.clientY,
        tlaBuildOrderId: target.tlaBuildOrderId,
        locked: target.locked,
      });
    },
    [],
  );

  const kodiakLineCompleteLockPendingBuildOrderId =
    kodiakLineCompleteLockMutation.isPending &&
    kodiakLineCompleteLockMutation.variables != null
      ? kodiakLineCompleteLockMutation.variables.tla_build_order_id
      : null;

  const closeKodiakLineCompleteLockMenu = useCallback(() => {
    setKodiakLineCompleteLockMenu(null);
  }, []);

  const handleKodiakToggleLineCompleteLock = (tlaBuildOrderId: number, lock: boolean): void => {
    closeKodiakLineCompleteLockMenu();
    kodiakLineCompleteLockMutation.mutate({ tla_build_order_id: tlaBuildOrderId, lock });
  };

  const handleKodiakAssignPrefix = (
    payload: KodiakPrefixDragPayload,
    target: { purchase_order_id: number; pcba_build_order_id: number },
  ): void => {
    const targetGroup = kodiakPcbaGroups.find((group) =>
      group.rows.some(
        (row) =>
          row.row_kind === "standard" &&
          Number(row.purchase_order_id) === Number(target.purchase_order_id) &&
          Number(row.pcba_build_order_id) === Number(target.pcba_build_order_id),
      ),
    );
    const targetRow =
      targetGroup?.rows.find((row) => row.row_kind === "standard") ??
      (kodiakMatchRows ?? []).find(
        (row) =>
          Number(row.purchase_order_id) === Number(target.purchase_order_id) &&
          Number(row.pcba_build_order_id) === Number(target.pcba_build_order_id) &&
          row.row_kind === "standard",
      );
    const targetCoreFeature =
      targetRow?.pcba_sku == null ? null : kodiakPcbaSkuCoreFeature(targetRow.pcba_sku);
    const targetTlaShortLabels =
      targetGroup == null ? [] : kodiakTlaShortLabelsForGroupRows(targetGroup.rows);
    if (
      !kodiakPrefixCanDropOnPcbaTarget(
        payload,
        {
          purchase_order_id: target.purchase_order_id,
          pcba_build_order_id: target.pcba_build_order_id,
          target_core_feature: targetCoreFeature,
          target_tla_short_labels: targetTlaShortLabels,
        },
        kodiakPrefixViewModel,
      )
    ) {
      const payloadPurchaseOrderId =
        payload.purchase_order_id == null ? null : Number(payload.purchase_order_id);
      if (
        payload.from_pcba_build_order_id != null &&
        payloadPurchaseOrderId != null &&
        payloadPurchaseOrderId === Number(target.purchase_order_id) &&
        Number(payload.from_pcba_build_order_id) === Number(target.pcba_build_order_id)
      ) {
        showToast({ tone: "error", message: "That prefix is already on this PCBA row." });
        return;
      }
      if (targetCoreFeature != null) {
        showToast({
          tone: "error",
          message: `Prefix ${payload.pcba_prefix}:${payload.core_feature} cannot move to this PCBA row — core feature or TLA variant does not match.`,
        });
      } else {
        showToast({ tone: "error", message: "This PCBA row is not a valid drop target for that prefix." });
      }
      return;
    }
    persistKodiakPrefixAssignment(
      target.purchase_order_id,
      payload.pcba_prefix,
      payload.core_feature,
      target.pcba_build_order_id,
    );
  };

  const handleKodiakAssignPrefixToTla = (
    payload: KodiakPrefixDragPayload,
    target: KodiakTlaPrefixDropTarget,
  ): void => {
    if (
      !kodiakPrefixCanDropOnTlaTarget(
        payload,
        {
          purchase_order_id: target.purchase_order_id,
          pcba_build_order_id: target.pcba_build_order_id,
          target_tla_sku: target.tla_sku,
          target_core_feature: target.target_core_feature,
        },
        kodiakPrefixViewModel,
      )
    ) {
      if (target.target_core_feature != null) {
        showToast({
          tone: "error",
          message: `Prefix ${payload.pcba_prefix}:${payload.core_feature} cannot move to this TLA row — core feature or TLA variant does not match.`,
        });
      } else {
        showToast({ tone: "error", message: "This TLA row is not a valid drop target for that prefix." });
      }
      return;
    }

    persistKodiakPrefixAssignment(
      target.purchase_order_id,
      payload.pcba_prefix,
      payload.core_feature,
      target.pcba_build_order_id,
      target.tla_build_order_id,
      target.tla_sku,
      target.tla_work_order_number,
    );
  };

  const handleKodiakUnallocatePrefix = (payload: KodiakPrefixDragPayload): void => {
    const purchaseOrderId = resolveKodiakPrefixDragPurchaseOrderId(payload, kodiakPrefixViewModel);
    if (purchaseOrderId == null) {
      showToast({ tone: "error", message: "Could not resolve purchase order for this prefix assignment." });
      return;
    }
    persistKodiakPrefixAssignment(purchaseOrderId, payload.pcba_prefix, payload.core_feature, null);
  };

  const handleKodiakPrefixDragStart = (event: DragEvent<HTMLSpanElement>, payload: KodiakPrefixDragPayload): void => {
    setActiveKodiakPrefixDragPayload(payload);
    event.dataTransfer.setData("application/x-kodiak-pcba-prefix", JSON.stringify(payload));
    event.dataTransfer.effectAllowed = "move";
  };

  function setActiveTab(tab: TlaPlanningTab): void {
    const next = new URLSearchParams(searchParams);
    if (tab === "kodiak") {
      next.set("tab", "kodiak");
    } else {
      next.delete("tab");
    }
    setSearchParams(next, { replace: true });
  }

  function toggleKodiakSort(nextKey: string): void {
    const key = nextKey as KodiakSortKey;
    if (kodiakSortKey === key) {
      setKodiakSortDir((previous) => (previous === "asc" ? "desc" : "asc"));
      return;
    }
    setKodiakSortKey(key);
    setKodiakSortDir(key === "po_date" || key === "unique_serial_count" ? "desc" : "asc");
  }

  useEffect(() => {
    const message = capacityQuery.error instanceof Error ? capacityQuery.error.message : null;
    if (message != null && message !== lastCapacityErrorRef.current) {
      lastCapacityErrorRef.current = message;
      showToast({ tone: "error", message: `TLA planning load failed: ${message}` });
    }
    if (capacityQuery.error == null) {
      lastCapacityErrorRef.current = null;
    }
  }, [capacityQuery.error, showToast]);

  useEffect(() => {
    const message = lineDetailsError?.message ?? null;
    if (message != null && message !== lastLineDetailsErrorRef.current) {
      lastLineDetailsErrorRef.current = message;
      showToast({ tone: "error", message: `TLA line breakdown load failed: ${message}` });
    }
    if (lineDetailsError == null) {
      lastLineDetailsErrorRef.current = null;
    }
  }, [lineDetailsError, showToast]);

  const patchReorderToMutation = useMutation({
    mutationFn: (variables: { sku: string; location_id: number; reorder_to: number; rowKey: string }) =>
      apiFetch<{ reorder_to: number }>("/api/mrp/tla-planning/reorder-to", {
        method: "PATCH",
        body: {
          sku: variables.sku,
          location_id: variables.location_id,
          reorder_to: variables.reorder_to,
        },
      }),
    onMutate: (variables) => {
      setReorderCommitFeedbackByKey((previous) => {
        const next = { ...previous };
        delete next[variables.rowKey];
        return next;
      });
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<TlaPlanningCapacityCache>(["mrp", "tla-planning", "capacity"], (cache) => {
        if (cache == null) {
          return cache;
        }
        return {
          rows: applyTlaPlanningReorderToToCapacityRows(
            cache.rows,
            variables.sku,
            variables.location_id,
            data.reorder_to,
          ),
        };
      });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "tla-planning", "capacity"] });
    },
    onSettled: (_data, error, variables) => {
      if (error != null || variables == null) {
        return;
      }
      setReorderCommitFeedbackByKey((previous) => ({
        ...previous,
        [variables.rowKey]: { succeeded: true },
      }));
    },
    onError: (error, variables) => {
      const message = error instanceof Error ? error.message : "Reorder to save failed.";
      showToast({ tone: "error", message });
      setReorderCommitFeedbackByKey((previous) => ({
        ...previous,
        [variables.rowKey]: { error: message },
      }));
    },
  });

  const overviewSummary = useMemo(() => {
    let totalSupply = 0;
    let totalDemand = 0;
    for (const row of capacityRows) {
      totalSupply += row.total_remaining_build_capacity;
      totalDemand += row.pending_sales_quantity;
    }
    return { rowCount: capacityRows.length, totalSupply, totalDemand };
  }, [capacityRows]);

  function selectOverviewRow(row: TlaPlanningCapacityRow) {
    const rowKey = tlaPlanningOverviewRowKey(row);
    if (selectedOverviewRowKey === rowKey) {
      setSelectedOverviewRowKey(null);
      return;
    }
    setSelectedOverviewRowKey(rowKey);
  }

  function handleOverviewRowKeyDown(
    event: KeyboardEvent<HTMLTableRowElement>,
    row: TlaPlanningCapacityRow,
  ): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectOverviewRow(row);
    }
  }

  function openDetail(row: TlaPlanningCapacityRow) {
    const locationId = tlaPlanningRowLocationId(row);
    if (locationId == null) {
      return;
    }
    const baseSearch = `?sku=${encodeURIComponent(row.sku)}&location_id=${String(locationId)}`;
    const target = buildNavigateTargetWithTrail({
      pathname: "/mrp/tla-planning/detail",
      baseSearch,
      trail,
    });
    navigate({ pathname: target.pathname, search: target.search }, { state: target.state });
  }

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint={PAGE_HOVER_HINT}>TLA Planning</PageTitleWithHelp>

      <div
        className="flex flex-wrap gap-1 border-b border-slate-200 pb-px"
        role="tablist"
        aria-label="TLA planning sections"
      >
        <button
          type="button"
          role="tab"
          id="tla-planning-tab-overview"
          aria-selected={activeTab === "overview"}
          aria-controls="tla-planning-panel-overview"
          className={cn(
            "rounded-t-md px-3 py-2 text-sm font-medium transition-colors",
            activeTab === "overview"
              ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
              : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
          )}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          type="button"
          role="tab"
          id="tla-planning-tab-kodiak"
          aria-selected={activeTab === "kodiak"}
          aria-controls="tla-planning-panel-kodiak"
          className={cn(
            "rounded-t-md px-3 py-2 text-sm font-medium transition-colors",
            activeTab === "kodiak"
              ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
              : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
          )}
          onClick={() => setActiveTab("kodiak")}
        >
          Kodiak TLA
        </button>
      </div>

      {activeTab === "overview" ? (
        <div role="tabpanel" id="tla-planning-panel-overview" aria-labelledby="tla-planning-tab-overview">
        <Card className="w-fit max-w-full">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 px-3 py-3">
            <CardTitleWithHelp hoverHint={OVERVIEW_HOVER_HINT}>TLA status</CardTitleWithHelp>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void refreshTlaPlanningOverview()}
              disabled={overviewRefreshing}
            >
              {overviewRefreshing ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              )}
              Refresh
            </Button>
          </CardHeader>
          <CardBody className="space-y-3 px-3 py-3">
            {capacityQuery.isLoading && capacityQuery.data == null && overviewSessionBootstrap.payload == null ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : null}
            {capacityQuery.data != null && !capacityQuery.error ? (
              <p className="text-sm text-slate-600">
                {overviewSummary.rowCount} rows · supply remaining{" "}
                <span className="tabular-nums">{formatNumber(overviewSummary.totalSupply)}</span> · pending demand{" "}
                <span className="tabular-nums">{formatNumber(overviewSummary.totalDemand)}</span>
                {overviewRefreshing ? (
                  <span className="text-slate-500"> · refreshing…</span>
                ) : null}
              </p>
            ) : null}
            <div className="w-fit max-w-full overflow-x-auto">
              <table className="w-max table-fixed text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <TlaPlanningTableHeaderWithHelp
                      label="Location"
                      hoverHint={OVERVIEW_COL_LOCATION_HINT}
                      className={STICKY_COL_CLASS}
                      labelClassName={OVERVIEW_HEADER_LOCATION_LABEL_CLASS}
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="SKU"
                      hoverHint={OVERVIEW_COL_SKU_HINT}
                      className={OVERVIEW_SKU_CELL_CLASS}
                      labelClassName={OVERVIEW_HEADER_SKU_LABEL_CLASS}
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Qty ordered"
                      hoverHint={OVERVIEW_COL_QTY_ORDERED_HINT}
                      className={OVERVIEW_NUM_CELL_CLASS}
                      labelClassName={OVERVIEW_HEADER_NUM_LABEL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Supply remaining"
                      hoverHint={OVERVIEW_COL_SUPPLY_REMAINING_HINT}
                      className={OVERVIEW_NUM_CELL_CLASS}
                      labelClassName={OVERVIEW_HEADER_NUM_LABEL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Pending demand"
                      hoverHint={OVERVIEW_COL_PENDING_DEMAND_HINT}
                      className={OVERVIEW_NUM_CELL_CLASS}
                      labelClassName={OVERVIEW_HEADER_NUM_LABEL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Net remaining"
                      hoverHint={OVERVIEW_COL_NET_REMAINING_HINT}
                      className={OVERVIEW_NUM_CELL_CLASS}
                      labelClassName={OVERVIEW_HEADER_NUM_LABEL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Reorder to"
                      hoverHint={OVERVIEW_COL_REORDER_TO_HINT}
                      className={OVERVIEW_NUM_CELL_CLASS}
                      labelClassName={OVERVIEW_HEADER_NUM_LABEL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Amount to order"
                      hoverHint={OVERVIEW_COL_AMOUNT_TO_ORDER_HINT}
                      className={OVERVIEW_NUM_CELL_CLASS}
                      labelClassName={OVERVIEW_HEADER_NUM_LABEL_CLASS}
                      align="right"
                    />
                  </tr>
                </thead>
                <tbody>
                  {capacityRows.map((row) => {
                    const locationId = tlaPlanningRowLocationId(row);
                    const lineKey = tlaPlanningOverviewKey(
                      row.sku,
                      tlaPlanningOverviewLineLocationId(row),
                    );
                    const rowKey = tlaPlanningOverviewRowKey(row);
                    const reorderFeedback = reorderCommitFeedbackByKey[rowKey];
                    const reorderSaving =
                      patchReorderToMutation.isPending &&
                      patchReorderToMutation.variables?.rowKey === rowKey;
                    const isSelected = selectedOverviewRowKey === rowKey;
                    return (
                    <tr
                      key={rowKey}
                      role="button"
                      tabIndex={0}
                      aria-selected={isSelected}
                      onClick={() => selectOverviewRow(row)}
                      onKeyDown={(event) => handleOverviewRowKeyDown(event, row)}
                      className={cn(
                        "cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50/90",
                        isSelected ? OVERVIEW_ROW_SELECTED_CLASS : undefined,
                      )}
                    >
                      <td className={cn(tlaPlanningStickyCellClass(isSelected), "text-left text-slate-900")}>
                        {row.location_name}
                      </td>
                      <td className={OVERVIEW_SKU_CELL_CLASS}>
                        <button
                          type="button"
                          className="text-left font-medium text-brand-700 hover:underline"
                          onClick={(event) => {
                            event.stopPropagation();
                            openDetail(row);
                          }}
                        >
                          {stripAltTlaSkuPrefixForDisplay(row.sku)}
                        </button>
                      </td>
                      <td className="p-0">
                        <TlaPlanningQtyOrderedCell
                          row={row}
                          qtyOrderedLines={lineDetailsQuery.data?.qty_ordered_by_key?.[lineKey]}
                          linesPending={lineDetailsQuery.isPending}
                          linesError={lineDetailsError}
                        />
                      </td>
                      <td className="p-0">
                        <TlaPlanningSupplyRemainingCell
                          row={row}
                          supplyLines={lineDetailsQuery.data?.supply_by_key[lineKey]}
                          linesPending={lineDetailsQuery.isPending}
                          linesError={lineDetailsError}
                        />
                      </td>
                      <td className="p-0">
                        <TlaPlanningPendingDemandCell
                          row={row}
                          demandLines={lineDetailsQuery.data?.demand_by_key[lineKey]}
                          linesPending={lineDetailsQuery.isPending}
                          linesError={lineDetailsError}
                        />
                      </td>
                      <td
                        className={cn(
                          OVERVIEW_NUM_CELL_CLASS,
                          row.net_remaining < 0 ? "font-medium text-red-700" : undefined,
                        )}
                      >
                        {formatNumber(row.net_remaining)}
                      </td>
                      <TlaPlanningReorderToCell
                        row={row}
                        canEdit={hubMrpAdminAllowed}
                        saving={reorderSaving}
                        commitError={reorderFeedback?.error ?? null}
                        commitSucceeded={
                          reorderFeedback?.succeeded === true && !reorderSaving
                        }
                        onInvalidCommit={(message) => showToast({ tone: "error", message })}
                        onCommit={(reorderTo) => {
                          if (locationId == null) {
                            return;
                          }
                          patchReorderToMutation.mutate({
                            sku: row.sku,
                            location_id: locationId,
                            reorder_to: reorderTo,
                            rowKey,
                          });
                        }}
                      />
                      <td
                        className={cn(
                          OVERVIEW_NUM_CELL_CLASS,
                          locationId != null && row.amount_to_order > 0 ? "font-medium text-amber-800" : undefined,
                          locationId == null ? "text-slate-400" : undefined,
                        )}
                      >
                        {locationId == null
                          ? "—"
                          : row.amount_to_order > 0
                            ? formatNumber(row.amount_to_order)
                            : "—"}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {capacityQuery.isError ? (
              <p className="text-sm text-red-700">
                {capacityQuery.error instanceof Error
                  ? capacityQuery.error.message
                  : "TLA planning failed to load."}
              </p>
            ) : !capacityQuery.isLoading && capacityRows.length === 0 ? (
              <p className="text-sm text-slate-500">No TLA supply or demand rows.</p>
            ) : null}
          </CardBody>
        </Card>
        </div>
      ) : null}

      {activeTab === "kodiak" ? (
        <div role="tabpanel" id="tla-planning-panel-kodiak" aria-labelledby="tla-planning-tab-kodiak">
        <Card className="w-full max-w-full">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 px-3 py-3">
            <CardTitleWithHelp hoverHint={KODIAK_TAB_HOVER_HINT}>Kodiak TLA / PCBA match</CardTitleWithHelp>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void kodiakMatchQuery.refetch()}
              disabled={kodiakMatchQuery.isFetching}
            >
              {kodiakShowRefreshing ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              )}
              Refresh
            </Button>
          </CardHeader>
          <CardBody className="space-y-3 px-3 py-3">
            {kodiakShowBlockingLoad ? (
              <p className="text-sm text-slate-500">
                <Loader2 className="mr-1 inline h-4 w-4 animate-spin" aria-hidden />
                Loading Kodiak match…
              </p>
            ) : null}
            {kodiakMatchQuery.error instanceof Error ? (
              <p className="text-sm text-red-600">{kodiakMatchQuery.error.message}</p>
            ) : null}
            {kodiakHasDisplayableData ? (
              <div className="space-y-3">
                <KodiakUnallocatedPrefixCard
                  prefixes={kodiakPrefixViewModel.unallocated_prefixes}
                  discovered_by_variant={kodiakPrefixViewModel.discovered_by_variant}
                  discovered_hover_by_variant={kodiakPrefixViewModel.discovered_hover_by_variant}
                  planned_by_variant={kodiakPrefixViewModel.planned_by_variant}
                  disabled={kodiakPrefixAssignmentMutation.isPending}
                  adding={kodiakManualPrefixMutation.isPending}
                  onAddPrefix={(pcbaPrefix) => kodiakManualPrefixMutation.mutate({ pcba_prefix: pcbaPrefix })}
                  onDropUnallocate={handleKodiakUnallocatePrefix}
                  onDragStartPrefix={handleKodiakPrefixDragStart}
                />
              </div>
            ) : null}
            {kodiakHasDisplayableData ? (
              <p className="text-sm text-slate-600">
                {kodiakMatchSummaryLabel(kodiakMatchRows?.length ?? 0, kodiakPcbaGroups.length)}
                {kodiakShowRefreshing ? (
                  <span className="ml-2 text-slate-500">Refreshing…</span>
                ) : null}
              </p>
            ) : null}
            {kodiakHasDisplayableData ? (
            <div className="w-full max-w-full overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <TlaPlanningSortableTableHeader label="PO" hoverHint={KODIAK_COL_PO_HINT} sortKey="po_number" activeSortKey={kodiakSortKey} sortDir={kodiakSortDir} onSort={toggleKodiakSort} className="px-3 py-2" />
                    <TlaPlanningSortableTableHeader label="PO date" hoverHint={KODIAK_COL_PO_HINT} sortKey="po_date" activeSortKey={kodiakSortKey} sortDir={kodiakSortDir} onSort={toggleKodiakSort} className="px-3 py-2" />
                    <TlaPlanningSortableTableHeader label="PO qty" hoverHint={KODIAK_COL_PO_HINT} sortKey="po_quantity" activeSortKey={kodiakSortKey} sortDir={kodiakSortDir} onSort={toggleKodiakSort} className={TLA_PLANNING_NUM_CELL_CLASS} align="right" />
                    <TlaPlanningSortableTableHeader label="PCBA WO" hoverHint={KODIAK_COL_PCBA_WO_HINT} sortKey="pcba_work_order_number" activeSortKey={kodiakSortKey} sortDir={kodiakSortDir} onSort={toggleKodiakSort} className="px-3 py-2" />
                    <TlaPlanningSortableTableHeader label="PCBA SKU" hoverHint={KODIAK_COL_PCBA_WO_HINT} sortKey="pcba_sku" activeSortKey={kodiakSortKey} sortDir={kodiakSortDir} onSort={toggleKodiakSort} className="px-3 py-2" />
                    <TlaPlanningSortableTableHeader label="PCBA qty" hoverHint={KODIAK_COL_PCBA_WO_HINT} sortKey="pcba_work_order_qty" activeSortKey={kodiakSortKey} sortDir={kodiakSortDir} onSort={toggleKodiakSort} className={TLA_PLANNING_NUM_CELL_CLASS} align="right" />
                    <TlaPlanningSortableTableHeader label="PCBA prefix" hoverHint={KODIAK_COL_PCBA_PREFIX_HINT} sortKey="pcba_prefix_estimate" activeSortKey={kodiakSortKey} sortDir={kodiakSortDir} onSort={toggleKodiakSort} className="px-3 py-2" />
                    <TlaPlanningSortableTableHeader label="TLA WO" hoverHint={KODIAK_COL_TLA_WO_HINT} sortKey="tla_work_order_number" activeSortKey={kodiakSortKey} sortDir={kodiakSortDir} onSort={toggleKodiakSort} className="px-3 py-2" />
                    <TlaPlanningSortableTableHeader label="TLA SKU" hoverHint={KODIAK_COL_TLA_SKU_HINT} sortKey="tla_sku" activeSortKey={kodiakSortKey} sortDir={kodiakSortDir} onSort={toggleKodiakSort} className="px-3 py-2" />
                    <TlaPlanningSortableTableHeader label="TLA qty" hoverHint={KODIAK_COL_TLA_WO_HINT} sortKey="tla_work_order_qty" activeSortKey={kodiakSortKey} sortDir={kodiakSortDir} onSort={toggleKodiakSort} className={TLA_PLANNING_NUM_CELL_CLASS} align="right" />
                    <TlaPlanningSortableTableHeader label="Serial count" hoverHint={KODIAK_COL_SERIAL_COUNT_HINT} sortKey="unique_serial_count" activeSortKey={kodiakSortKey} sortDir={kodiakSortDir} onSort={toggleKodiakSort} className={TLA_PLANNING_NUM_CELL_CLASS} align="right" />
                  </tr>
                </thead>
                <tbody>
                  <KodiakTlaPcbaMatchTableBody
                    groups={kodiakPcbaGroups}
                    prefixViewModel={kodiakPrefixViewModel}
                    prefixAssignmentDisabled={kodiakPrefixAssignmentMutation.isPending}
                    onAssignPrefix={handleKodiakAssignPrefix}
                    onAssignPrefixToTla={handleKodiakAssignPrefixToTla}
                    onDragStartPrefix={handleKodiakPrefixDragStart}
                    lineCompleteLockIds={kodiakLineCompleteLockIds}
                    lineCompleteLockAdminAllowed={hubMrpAdminAllowed}
                    lineCompleteLockPendingBuildOrderId={kodiakLineCompleteLockPendingBuildOrderId}
                    onSerialCountContextMenu={handleKodiakSerialCountContextMenu}
                  />
                </tbody>
              </table>
              <KodiakLineCompleteLockContextMenuPanel
                menu={kodiakLineCompleteLockMenu}
                pendingBuildOrderId={kodiakLineCompleteLockPendingBuildOrderId}
                onClose={closeKodiakLineCompleteLockMenu}
                onToggleLock={handleKodiakToggleLineCompleteLock}
              />
            </div>
            ) : null}
            {kodiakHasDisplayableData && (kodiakMatchRows?.length ?? 0) === 0 && !kodiakShowRefreshing ? (
              <p className="text-sm text-slate-500">No Kodiak TLA / PCBA match rows.</p>
            ) : null}
          </CardBody>
        </Card>
        </div>
      ) : null}
    </div>
  );
}
